// TODO: 장기적으로 shadcn Combobox 패턴(Popover + Command)으로 교체 예정
// - 접근성(ARIA listbox/combobox), 키보드 네비게이션, 포커스 관리 개선
// - 현재는 커스텀 구현이며, scenario 도메인 단일 소비자 전용
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { MultiSelectProps } from './MultiSelect.types';

const MultiSelect = ({
  options,
  placeholder = 'Choose Options...',
  disabled = false,
  value = [],
  onChange,
}: MultiSelectProps): JSX.Element => {
  const [selected, setSelected] = useState<string[]>(value);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (item: string) => {
    let newSelected: string[];
    if (selected.includes(item)) {
      newSelected = selected.filter((s) => s !== item);
    } else {
      newSelected = [...selected, item];
    }
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  const removeItem = (item: string) => {
    const newSelected = selected.filter((s) => s !== item);
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div
        role="combobox"
        aria-expanded={open}
        tabIndex={disabled ? -1 : 0}
        className={cn(
          'border rounded-[3px] px-[8px] py-[2px] flex flex-wrap gap-1 min-h-[36px] overflow-auto',
          disabled
            ? 'bg-[var(--neutral-200)] border-[var(--neutral-800)]'
            : 'border-[var(--neutral-800)] bg-[var(--neutral-50)] cursor-pointer'
        )}
        onClick={() => !disabled && setOpen(!open)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(!open);
          }
        }}
      >
        {selected.length === 0 && (
          <span className="text-gray-400 title3 flex items-center">{placeholder}</span>
        )}

        {selected.map((selectedValue) => {
          const option = options.find((opt) => opt.value === selectedValue);
          const displayLabel = option?.item || selectedValue;
          return (
            <div
              key={selectedValue}
              className="flex items-center bg-[var(--btn-dfilter)] text-[var(--active-white-font)] px-1 py-1 rounded-md title3"
            >
              {displayLabel}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeItem(selectedValue);
                }}
                className="ml-1 text-[var(--active-white-font)] opacity-70 hover:opacity-100"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 right-0 border border-[var(--neutral-800)] rounded-md mt-1 max-h-40 overflow-y-auto bg-[var(--neutral-50)] shadow-md z-10">
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              onClick={() => toggleOption(option.value)}
              className={cn(
                'w-full justify-between px-3 py-2 cursor-pointer hover:bg-[var(--neutral-100)] flex items-center text-[var(--font-color)]',
                selected.includes(option.value) && 'bg-[var(--neutral-200)] font-semibold'
              )}
            >
              <span className="text-[12px]">{option.item}</span>
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                readOnly
                className="mr-2"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
